import { Box, Button, Card, CardBody, Flex, Heading, Link, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, Wrap, WrapItem } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { PortalEventScope, useGetAuthenticatedPortalSourcingEventsQuery } from "../../../autogen/bff-api";
import { FilterChip } from "../../../common/components/FilterChip";
import { displayDateWithTime } from "../../../common/formatting/displayDateWithTime";
import { momentLocaleFor } from "../../../common/formatting/i18nFormatters";
import { urls } from "../../../urls";
import { PortalLifecycleTag } from "../../sourcing-events/portal/components/PortalLifecycleTag";

const DISPLAY_LIMIT = 50;

export const SupplierInvitationsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = momentLocaleFor(i18n.language);
  const [showExpired, setShowExpired] = useState(false);

  const { data, isLoading, isError } = useGetAuthenticatedPortalSourcingEventsQuery({
    scope: showExpired ? PortalEventScope.InvitationsIncludingExpired : PortalEventScope.ActiveInvitations,
    limit: DISPLAY_LIMIT,
  });
  const events = data?.events ?? [];

  const emptyMessage = showExpired ? t("You have no invitations") : t("You have no active invitations");

  return (
    <>
      <Heading as="h1" size="lg">
        {t("My invitations")}
      </Heading>
      <Text color="smMuted" mt="2" mb="6">
        {t("Invitations you have not answered yet.")}{" "}
        {t("Invitations you have answered are under")}{" "}
        <Link as={RouterLink} to={urls.v2.supplier.bids} color="smPrimary">
          {t("My bids")}
        </Link>
      </Text>

      <Wrap spacing="2" mb="5">
        <WrapItem>
          <FilterChip
            active={showExpired}
            label={t("Show expired")}
            onClick={() => setShowExpired((previous) => !previous)}
          />
        </WrapItem>
      </Wrap>

      {isLoading ? (
        <Flex justify="center" py="20">
          <Spinner color="smPrimary" />
        </Flex>
      ) : isError || events.length === 0 ? (
        <Card variant="outline" rounded="xl">
          <CardBody py="20">
            <Flex align="center" justify="center">
              <Text color="smMuted">{isError ? t("Could not load your invitations") : emptyMessage}</Text>
            </Flex>
          </CardBody>
        </Card>
      ) : (
        <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" overflowX="auto">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>{t("Event")}</Th>
                <Th>{t("Buyer")}</Th>
                <Th>{t("Deadline")}</Th>
                <Th>{t("Status")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {events.map((event) => (
                <Tr key={event.id}>
                  <Td width="80" maxWidth="80" overflow="hidden" textAlign="left">
                    <Button
                      variant="link"
                      colorScheme="teal"
                      textAlign="left"
                      whiteSpace="pre-wrap"
                      onClick={() => navigate(urls.v2.supplier.procurement.go(event.id))}
                    >
                      {event.title || t("Untitled")}
                    </Button>
                  </Td>
                  <Td>
                    <Text>{event.buyerName}</Text>
                  </Td>
                  <Td>
                    <Text>{displayDateWithTime(event.bidSubmissionDeadline, null, locale)}</Text>
                  </Td>
                  <Td>
                    <PortalLifecycleTag status={event.lifecycle.status} stage={event.competitionStage} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </>
  );
};
